import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Plus, ChevronDown, ChevronRight, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type Any = Record<string, any>;

interface Props {
  akte: Any;
  art?: string;
  stellungnahmeId?: string;
  onInsert: (text: string) => void;
}

const ERGEBNISSE = [
  { v: 'offen', l: 'Ergebnis offen' },
  { v: 'reguliert', l: 'Reguliert (erfolgreich)' },
  { v: 'teilweise', l: 'Teilweise reguliert' },
  { v: 'abgelehnt', l: 'Abgelehnt' },
];

const KATEGORIEN = [
  'stundenverrechnungssaetze',
  'verbringungskosten',
  'upe',
  'minderwert',
  'honorar',
  'nachbesichtigung',
  'restwert',
  'wbw',
  'nutzungsausfall',
  'sonstiges',
];

export default function StellungnahmeBausteine({ akte, art, stellungnahmeId, onInsert }: Props) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState('offen');

  // Neuer-Baustein-Formular
  const [showNew, setShowNew] = useState(false);
  const [nKat, setNKat] = useState('sonstiges');
  const [nTitel, setNTitel] = useState('');
  const [nArg, setNArg] = useState('');
  const [nRecht, setNRecht] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('stellungnahme_wissen')
      .select('id, kategorie, titel, argumentation, rechtsgrundlage, anwendungen, erfolgsquote')
      .eq('aktiv', true);
    setItems(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      await load();
      if (!aktiv) return;
    })();
    return () => {
      aktiv = false;
    };
  }, [load]);

  const relevant = (() => {
    if (art === 'honorar') return new Set(['honorar']);
    const s = new Set<string>(['minderwert', 'restwert']);
    if (akte?.rep_kosten_gekuerzt) ['stundenverrechnungssaetze', 'verbringungskosten', 'upe'].forEach((k) => s.add(k));
    if (akte?.nachbesichtigung_gefordert) s.add('nachbesichtigung');
    return s;
  })();

  const sorted = [...items]
    .map((b) => ({ ...b, _rel: relevant.has(b.kategorie) ? 1 : 0 }))
    .sort(
      (a, b) =>
        b._rel - a._rel ||
        (Number(b.erfolgsquote ?? -1) - Number(a.erfolgsquote ?? -1)) ||
        (b.anwendungen ?? 0) - (a.anwendungen ?? 0),
    );

  async function einfuegen(b: any) {
    onInsert(b.argumentation + (b.rechtsgrundlage ? ` (${b.rechtsgrundlage})` : ''));
    if (stellungnahmeId) {
      await supabase.from('stellungnahme_wissen_feedback').insert({
        wissen_id: b.id,
        stellungnahme_id: stellungnahmeId,
        akte_id: akte?.id ?? null,
        ergebnis: 'offen',
      });
    }
    setMsg(`„${b.titel}" eingefügt`);
    setTimeout(() => setMsg(null), 1500);
  }

  async function speichereErgebnis(e: string) {
    setErgebnis(e);
    if (!stellungnahmeId) return;
    await supabase.from('stellungnahme_wissen_feedback').update({ ergebnis: e }).eq('stellungnahme_id', stellungnahmeId);
    setMsg('Ergebnis gespeichert — Wissensbasis lernt daraus.');
    setTimeout(() => setMsg(null), 2200);
  }

  async function neuSpeichern() {
    if (!nTitel.trim() || !nArg.trim()) {
      setMsg('Titel und Argumentation sind nötig.');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('stellungnahme_wissen').insert({
      kategorie: nKat,
      titel: nTitel.trim(),
      argumentation: nArg.trim(),
      rechtsgrundlage: nRecht.trim() || null,
      quelle: 'manuell',
      aktiv: true,
    });
    setSaving(false);
    if (error) {
      setMsg('Fehler: ' + error.message);
      return;
    }
    setShowNew(false);
    setNTitel('');
    setNArg('');
    setNRecht('');
    setNKat('sonstiges');
    setMsg('Baustein gespeichert.');
    setTimeout(() => setMsg(null), 2000);
    await load();
  }

  return (
    <div className="border border-gray-200 rounded-xl">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-700"
      >
        <BookOpen className="w-4 h-4 text-[#1f3a5c]" />
        Argumentations-Bausteine (Wissensbasis)
        <span className="text-xs font-normal text-gray-400">{loading ? '' : `· ${sorted.length}`}</span>
        {msg && <span className="text-xs font-normal text-green-600 ml-2">{msg}</span>}
        <span className="ml-auto">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {loading ? (
            <p className="text-xs text-gray-400">Lädt…</p>
          ) : sorted.length === 0 ? (
            <p className="text-xs text-gray-500">Noch keine Bausteine hinterlegt.</p>
          ) : (
            <ul className="space-y-1.5">
              {sorted.map((b) => (
                <li key={b.id} className="flex items-start gap-2 border border-gray-100 rounded-lg px-2.5 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {b._rel === 1 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">empfohlen</span>
                      )}
                      <span className="text-xs font-medium text-gray-800">{b.titel}</span>
                      {b.erfolgsquote != null && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                          {b.erfolgsquote}% Erfolg
                        </span>
                      )}
                      {b.anwendungen > 0 && <span className="text-[10px] text-gray-400">{b.anwendungen}× genutzt</span>}
                    </div>
                    <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5">{b.argumentation}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => einfuegen(b)}
                    className="shrink-0 flex items-center gap-1 text-xs font-medium text-white bg-[#1f3a5c] hover:bg-[#16304d] px-2 py-1 rounded-md"
                  >
                    <Plus className="w-3 h-3" /> Einfügen
                  </button>
                </li>
              ))}
            </ul>
          )}

          {showNew ? (
            <div className="border border-gray-200 rounded-lg p-2.5 bg-gray-50/60 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Neuer Baustein</span>
                <button type="button" onClick={() => setShowNew(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-[11px] text-gray-500">
                  Kategorie
                  <select
                    className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-xs bg-white"
                    value={nKat}
                    onChange={(e) => setNKat(e.target.value)}
                  >
                    {KATEGORIEN.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-[11px] text-gray-500">
                  Titel
                  <input
                    className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-xs bg-white"
                    value={nTitel}
                    onChange={(e) => setNTitel(e.target.value)}
                    placeholder="Kurzer Name"
                  />
                </label>
              </div>
              <label className="text-[11px] text-gray-500 block">
                Argumentation
                <textarea
                  className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-xs bg-white min-h-[60px]"
                  value={nArg}
                  onChange={(e) => setNArg(e.target.value)}
                  placeholder="Der Textbaustein / die Vorgehensweise"
                />
              </label>
              <label className="text-[11px] text-gray-500 block">
                Rechtsgrundlage (optional)
                <input
                  className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-xs bg-white"
                  value={nRecht}
                  onChange={(e) => setNRecht(e.target.value)}
                  placeholder="z.B. BGH VI ZR 53/09"
                />
              </label>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={neuSpeichern}
                  disabled={saving}
                  className="text-xs font-medium text-white bg-[#1f3a5c] hover:bg-[#16304d] px-3 py-1 rounded-md disabled:opacity-50"
                >
                  {saving ? 'Speichert…' : 'Baustein speichern'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowNew(true)}
              className="flex items-center gap-1 text-xs font-medium text-[#1f3a5c] hover:underline"
            >
              <Plus className="w-3.5 h-3.5" /> Neuer Baustein
            </button>
          )}

          {stellungnahmeId && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-gray-500">Ergebnis (lernt):</span>
              <select
                className="text-xs rounded-md border border-gray-300 px-2 py-1"
                value={ergebnis}
                onChange={(e) => speichereErgebnis(e.target.value)}
              >
                {ERGEBNISSE.map((o) => (
                  <option key={o.v} value={o.v}>
                    {o.l}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
